import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  ChakraProps,
} from '@chakra-ui/react';

import { Markdown } from './markdown';

export type FaqAccordionItem = {
  question: string;
  answer: string;
};

export type FaqAccordionProps = {
  faqs: FaqAccordionItem[];
  /**
   * Chakra color scheme name used for the expanded item's border, background,
   * and text. Must be a palette with 50/100/300/500 shades.
   */
  colorScheme?: string;
  /**
   * Space between the accordion items. Accepts responsive values, e.g.
   * `{ base: '8px', md: '12px' }`. Items stack flush when omitted.
   */
  itemGap?: ChakraProps['gap'];
  onItemClick?: () => void;
};

export function FaqAccordion({ faqs, colorScheme = 'blue', itemGap, onItemClick }: FaqAccordionProps) {
  return (
    <Accordion
      display='flex'
      flexDirection='column'
      gap={itemGap}
      variant='v2'
      sx={{
        '.chakra-accordion__item:has(.chakra-accordion__button[aria-expanded="true"])': {
          borderColor: `${colorScheme}.300 !important`,
        },
        // The v2 variant's container slot hardcodes blue accents on the expanded
        // button with the same selector specificity and wins on insertion order;
        // routing through `.chakra-accordion__item` out-specifies it.
        '.chakra-accordion__item .chakra-accordion__button[aria-expanded="true"]': {
          bg: `${colorScheme}.50 !important`,
          borderBottomColor: `${colorScheme}.100 !important`,
          color: `${colorScheme}.500 !important`,
        },
        '.chakra-accordion__button:hover': {
          color: `${colorScheme}.500 !important`,
        },
      }}
      allowMultiple
    >
      {faqs.map((faq) => (
        <AccordionItem key={faq.question}>
          <h2>
            <AccordionButton onClick={onItemClick}>
              <Box as='span' flex='1' textAlign='left'>
                {faq.question}
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel>
            <Markdown markdownText={faq.answer} />
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
